import { action, configure, decorate } from 'mobx';
import ReactGA from 'react-ga';
import {
  removeStoredAuctionLaneIdByPath,
  setStoredAuctionIdByPath,
  setStoredAuctionLaneIdByPath,
  getStoredValue,
} from 'shared/utils/localStorage';

import { toBoolean } from 'stores/utils';
import { toNumber } from 'lodash';
import UiMessageCodeHelpers from 'shared/utils/UiMessageCodeHelpers';
import { messagePrettier } from './utils';
import {
  arrayContainsValue,
  getKeyCode,
  getTargetValue,
  isEnterKey,
  stripCurrencySymbols,
  trimmedStringContainsValue,
  trimmedStringMatchesExactly,
} from '../components/project/ClerkScreen/shared/utils/common';
configure({ enforceActions: 'always' });

export default class UIStore {
  constructor(WebSocketStore) {
    this.webSocketStore = WebSocketStore;
    this.bidStore = this.webSocketStore.bidStore;
    this.auctionStore = this.webSocketStore.auctionStore;
    this.messageStore = this.webSocketStore.messageStore;
    this.providerStore = this.webSocketStore.providerStore;
  }

  handleLogin = async (username, password) => {
    console.log('handleLogin - attempt to connect');
    await this.webSocketStore.connect(username, password);
  };

  handleStartEvent = async () => this.webSocketStore.connect();

  handleLogout = () => this.webSocketStore.disconnect();

  // TEMP: TOO MANY INITIALIZEBIDDING CALLS
  handleJoinAuction = async (auctionId) => {
    console.log('joining auction', auctionId);
    removeStoredAuctionLaneIdByPath();
    await this.webSocketStore.sendMessage({
      method: 'JoinAuction',
      payload: [auctionId],
    });
  };

  // TODO: When changing lanes, need to ensure we clear out any previous state from another lane
  handleJoinLane = async (laneId) => {
    console.log('joining lane', laneId);
    this.messageStore.directMessageLog = [];
    this.webSocketStore.sendFocusOpenItemMessage();
    await this.webSocketStore.sendMessage({
      method: 'ChangeLane',
      payload: [laneId],
    });
  };

  handleInitializeScreenAuctions = async (auctionId) => {
    this.auctionStore.layoutSettings
      .filter((item) => item.layoutType !== 'Bidder')
      .map((item) => setStoredAuctionIdByPath(auctionId, item.slug));
  };

  handleInitializeScreenLanes = async (laneId) => {
    this.auctionStore.layoutSettings
      .filter((item) => item.layoutType !== 'Bidder')
      .map((item) => setStoredAuctionLaneIdByPath(laneId, item.slug));
  };

  // TODO: Not really ending the lane (Re-Run button does not disable and we are able to click it and it pulls up last block item)
  // TODO: The lane id is not removed from local storage after clicking the end lane button
  handleEndLane = async (message) => {
    this.handleSendLaneMessage(message);
    this.webSocketStore.endLane();
  };

  handleRestartLane = async () => {
    await this.webSocketStore.sendSetLaneRunningMessage();
    this.bidStore.setConfirmBadgeIdState(false, true, null);
    this.bidStore.setProperty('floorBidderBadge', null);
    this.bidStore.resetConfirmations();
  };

  handleReJoinLastLane = () => {
    this.webSocketStore.sendFocusOpenItemMessage();
    this.webSocketStore.handleRejoin();
  };

  handleToggleLaneMedia = () => {
    const invocationMessage = this.auctionStore.laneMediaMuted
      ? 'UnmuteLaneMedia'
      : 'MuteLaneMedia';

    this.webSocketStore.sendMessage({
      method: invocationMessage,
      payload: [this.auctionStore.getAuctionLaneIdForInvocation],
    });
  };

  // TODO: Pause Lane doesn't disable page when Item is on Block and has been statused. Not until the Re-Run button is clicked does it work as we would expect.
  // TODO: Doesn't disable the Open Item# field after we click the RE-Run button.
  // TODO: Should disable the bid button (visually) and the asking bid (all controls really).
  handlePauseLane = () => {
    if (!this.bidStore.lanePaused) {
      this.bidStore.backupBlockStatus();
      this.webSocketStore.sendMessage({
        method: 'SetLanePausedStatus',
        payload: [this.auctionStore.getAuctionLaneIdForInvocation],
      });
      this.bidStore.setStartingBidAmountEntered(null);
      this.bidStore.setOpenItemNumberEntered(null);
      return;
    }
    this.handleRestartLane();
  };

  handlePlayAnnouncements = async (laneId) => {
    await this.webSocketStore.sendMessage({
      method: 'SendOnBlockHighlight',
      payload: [laneId, 20],
    });
  }

  handleStopAnnouncements = async (laneId) => {
    await this.webSocketStore.sendMessage({
      method: 'SendOnBlockHighlight',
      payload: [laneId, 21],
    });
  }

  handleExtendVATime = async (value, laneId) => {
    const {isVirtualRunning} = this.auctionStore;
    if (isVirtualRunning) {
      await this.webSocketStore.sendMessage({
        method: 'SendOnBlockCountdown',
        payload: [laneId, 1],
      });
    }
  }

  handleVAToggle = async (auctionId, laneId) => {
    console.log('auctionId', auctionId);
    const {isVirtualRunning} = this.auctionStore;
    console.log('toggle: isVirtualRunning', isVirtualRunning)
      // this.handleMonitorChatOnly(this.bidStore.auction['isVaRunning']);

    // this.bidStore.setToggleVA(!this.bidStore.auction['isVaRunning']);

    if (!isVirtualRunning) {
      await this.webSocketStore.sendMessage({
        method: 'StartVirtualAuctioneer',
        payload: [laneId],
      });
    }

    if (isVirtualRunning) {
      await this.webSocketStore.sendMessage({
        method: 'StopVirtualAuctioneer',
        payload: [laneId],
      });
    }

    this.auctionStore.setVirtualAuctioneerStatus({isVirtualRunning: !isVirtualRunning, isVirtual: true });

    console.log('start/stop');
  }

  handleSuspendLane = async () => {
    if (!this.bidStore.laneSuspended) {
      await this.webSocketStore.sendMessage({
        method: 'SetLaneSuspendedStatus',
        payload: [this.auctionStore.getAuctionLaneIdForInvocation],
      });
      this.bidStore.setStartingBidAmountEntered(null);
      this.bidStore.setOpenItemNumberEntered(null);
      return;
    }
    this.handleRestartLane();
  };

  handleResumeBidding = () => {
    this.webSocketStore.sendMessage({
      method: 'ResumeBidding',
      payload: [this.auctionStore.getAuctionLaneIdForInvocation],
    });
  };

  handleResume = () => {
    if (this.bidStore.isConfirmingDecision) {
      this.handleSuspendLane();
    } else {
      this.handleResumeBidding();
    }
    this.webSocketStore.sendFocusBidButtonMessage();
  };

  handleReRun = async () => {
    const {
      auction: { lotNumber },
    } = this.bidStore;
    await this.handleRestartLane();
    this.bidStore.initializeBidding(true);
    this.webSocketStore.sendFocusStartingBidAmountMessage();
    await this.webSocketStore.sendMessage({
      method: 'ReRunBidding',
      payload: [this.auctionStore.getAuctionLaneIdForInvocation],
    });
    await this.webSocketStore.sendMessage({
      method: 'GetProduct',
      payload: [this.auctionStore.getAuctionLaneIdForInvocation, lotNumber],
    });
  };

  handleBidIncrementTabClick = (value) => {
    this.bidStore.auction.selectedBidIncrementTabIndex = value;
  };

  handleSetBlockBidIncrement = (value) => {
    this.webSocketStore.sendMessage({
      method: 'SetBidIncrement',
      payload: [this.auctionStore.getAuctionLaneIdForInvocation, value],
    });
  };

  handleSetLaneBidIncrement = (value) =>
    this.webSocketStore.sendMessage({
      method: 'SetLaneBidIncrement',
      payload: [this.auctionStore.getAuctionLaneIdForInvocation, value],
    });

  handleBidIncrementClick = (value) => {
    this.bidStore.setProperty(
      'prevStatusBgColor',
      this.bidStore.auction.statusBgColor,
    );
    this.bidStore.setProperty(
      'prevStatusColor',
      this.bidStore.auction.statusColor,
    );

    const { selectedBidIncrementTabIndex } = this.bidStore.auction;
    if (selectedBidIncrementTabIndex === 0)
      this.handleSetLaneBidIncrement(value);
    if (selectedBidIncrementTabIndex === 1)
      this.handleSetBlockBidIncrement(value);
  };

  handleStartBidding = async (value) => {
    const startingBid = stripCurrencySymbols(value);

    this.bidStore.setProperty('isOpenForBiddingFromStartingBidAmount', true);
    try {
      this.webSocketStore.sendMessage({
        method: 'SetStartingBidAmount',
        payload: [this.auctionStore.getAuctionLaneIdForInvocation, startingBid],
      });
    } catch (e) {
      console.error(e);
      this.bidStore.setProperty('isOpenForBidding', false);
    }
  };

  handleFloorBidderBidClick = () => {
    this.webSocketStore.sendMessage({
      method: 'FloorBid',
      payload: [this.auctionStore.getAuctionLaneIdForInvocation],
    });
    this.webSocketStore.sendFocusBidButtonMessage();
  };

  handleSetReserveToWinningBidAmount = () => {
    this.webSocketStore.sendMessage({method:'SetReserveToWinningBidAmount', payload: [this.auctionStore.getAuctionLaneIdForInvocation],})
  }

  handleRetractBid = () => {
    this.bidStore.setProperty(
      'prevStatusBgColor',
      this.bidStore.auction.statusBgColor,
    );
    this.bidStore.setProperty(
      'prevStatusColor',
      this.bidStore.auction.statusColor,
    );

    this.webSocketStore.sendMessage({
      method: 'RetractBid',
      payload: [this.auctionStore.getAuctionLaneIdForInvocation],
    });
  };

  handleFloorBidderBadgeChange = (event) => {
    event.persist();
    this.bidStore.setProperty('floorBidderBadge', event.target.value);
  };

  handleCloseBidding = async (state, badgeId = null) => {
    let badgeIdToUse = null;
    try {
      badgeIdToUse = this.bidStore.auction.floorBidderBadge || badgeId;
      const currentUiState = UiMessageCodeHelpers.getUiStateResult(
        this.bidStore.auction.uiDecisionCode,
      );

      const key = (state || currentUiState?.key).replace('Confirm', '');

      this.bidStore.handleCloseBidding(key);

      await this.webSocketStore.sendMessage({
        method: 'CloseBidding',
        payload: [
          this.auctionStore.getAuctionLaneIdForInvocation,
          key,
          badgeIdToUse,
        ],
      });

      await this.webSocketStore.sendSetLaneRunningMessage();
    } catch (err) {
      console.error('handleCloseBidding', { badgeIdToUse, err, state });
    }
  };

  /** Direct Messaging */
  handleSendDirectMessage = (member, account, message, itemNumber) => {
    const {
      auction: { lotNumber },
    } = this.bidStore;

    if (messagePrettier(message).length === 0) return;

    try {
      this.webSocketStore.sendMessage({
        method: 'MessageToClient',
        payload: [
          this.auctionStore.getAuctionLaneIdForInvocation,
          account,
          member,
          message,
          itemNumber || lotNumber,
        ],
      });
    } catch (error) {
      console.error('handleSendDirectMessage', {
        account,
        error,
        itemOrLotNumber: itemNumber || lotNumber,
        laneId: this.auctionStore.getAuctionLaneIdForInvocation,
        member,
        message,
      });
    }
  };

  handleSendLaneMessage = (message) => {

    if (messagePrettier(message).length === 0) return;

    try {
      this.webSocketStore.sendMessage({
        method: 'MessageToLane',
        payload: [this.auctionStore.getAuctionLaneIdForInvocation, message],
      });
    } catch (error) {
      console.error('handleSendLaneMessage', { error, message });
    }
  };

  handleSetLanePinMessage = (message) => {

    if (messagePrettier(message).length === 0) return;

    try {
      this.webSocketStore.sendMessage({
        method: 'SetLanePin',
        payload: [this.auctionStore.getAuctionLaneIdForInvocation, message],
      });
    } catch (error) {
      console.error('handleSendPinMessage', { error, message });
    }
  };

  handleClearLanePinMessage = () => {
    try {
      // TODO: THIS NEEDS TO BE UPDATED TO CLEAR LANE PIN AND PASS LANE ID
      this.webSocketStore.sendMessage({
        method: 'ClearLanePin',
        payload: [this.auctionStore.getAuctionLaneIdForInvocation],
      });
    } catch (error) {
      console.error('handleClearPinMessage', error);
    }
  };

  handleFloorBidderOverride = () =>
    this.webSocketStore.sendMessage({
      method: 'FloorBidOverride',
      payload: [this.auctionStore.getAuctionLaneIdForInvocation],
    });

  setEditingFloorBidderBadge = () =>
    this.webSocketStore.sendFocusFloorBidderBadgeMessage();

  closeNoSaleAndReset = () => {
    const { resetConfirmations, setConfirmBadgeIdState } = this.bidStore;

    setConfirmBadgeIdState(false, true, null);
    this.handleCloseBidding('NoSale');
    this.setEditingFloorBidderBadge();
    resetConfirmations();
  };

  closeBiddingAndReset = (value) => {
    const { resetConfirmations, setConfirmBadgeIdState } = this.bidStore;
    const uiMessageCodeResults = UiMessageCodeHelpers.getUiStateResult(value);
    setConfirmBadgeIdState(false, true, null);
    this.handleCloseBidding(uiMessageCodeResults.key);
    this.setEditingFloorBidderBadge();
    resetConfirmations();
  };

  handleDecisionClick = (key) => {
    const uiCodeKey = this.bidStore.laneSuspended ? `Confirm${key}` : key;
    const uiCodes = UiMessageCodeHelpers.uiMessageState;
    this.webSocketStore.sendMessage({
      method: 'UiMessageToClerks',
      payload: [
        this.auctionStore.getAuctionLaneIdForInvocation,
        uiCodes[uiCodeKey],
      ],
    });

    this.handleDecision(key);
  };

  handleDecision = (value) => {
    const {
      auction,
      isConfirmingDecision,
      lanePaused,
      laneSuspended,
      noFloorBidderBadgeEntered,
      setConfirmBadgeIdState,
      setConfirmingDecisionClick,
      setFirstDecisionClick,
      setUserActivityLog,
      shouldDisableFloorBidderBadge,
      showUserActivityLog,
    } = this.bidStore;

    const uiMessageCodeResults = UiMessageCodeHelpers.getUiStateResult(value);

    if (lanePaused) {
      setUserActivityLog('Lane Paused, DO NOT PROCEED WITH SALE...');
      showUserActivityLog();
      return;
    }

    // TODO: When online bidder, decision buttons are forcing a double click to confirm when it should need to be a single click
    if (isConfirmingDecision) {
      setConfirmingDecisionClick(uiMessageCodeResults.key);
      setUserActivityLog(
        'Start Confirming Block (2nd button click)',
        uiMessageCodeResults,
      );
      if (!shouldDisableFloorBidderBadge) {
        if (
          noFloorBidderBadgeEntered &&
          value !== 'NoSale' &&
          value !== 'ConfirmNoSale'
        ) {
          setUserActivityLog(
            `Confirming 1.1 (Sold/SubectTo clicked; FBB enabled, not entered); FBB focus -- ${value} -- ${uiMessageCodeResults.value} -- ${uiMessageCodeResults.key}`,
          );
          setConfirmBadgeIdState(false, true, null);
          this.setEditingFloorBidderBadge();

          // You tried to confirm with an invalid FBB, so ui state remains the first button click
          this.webSocketStore.sendMessage({
            method: 'UiMessageToClerks',
            payload: [
              this.auctionStore.getAuctionLaneIdForInvocation,
              UiMessageCodeHelpers.getUiStateResult(auction.firstDecisionClick)
                .value,
            ],
          });
        } else {
          // FBB ENTERED
          setUserActivityLog(
            `Confirming 1.2 (${value} clicked); Close bid -- ${uiMessageCodeResults.value} -- ${uiMessageCodeResults.key}`,
          );
          if (value === 'NoSale' || value === 'ConfirmNoSale') {
            this.webSocketStore.sendMessage({
              method: 'UiMessageToClerks',
              payload: [
                this.auctionStore.getAuctionLaneIdForInvocation,
                uiMessageCodeResults.value,
              ],
            });

            this.closeNoSaleAndReset();
            showUserActivityLog();
          } else {
            setConfirmBadgeIdState(false, true, value);
            this.closeBiddingAndReset(value);
          }
        }
      } else {
        // confirming online bidder
        setUserActivityLog(
          `Confirming 1.3: Online Bidder - ${auction.winningBidderName} (${auction.winningBidderNumber})`,
        );
        this.closeBiddingAndReset(value);
      }
    } else if (!laneSuspended) {
      this.handleSuspendLane(uiMessageCodeResults.value);
      setFirstDecisionClick(value);
      setUserActivityLog('Start Not Confirming Block (1st button click)');
      this.setEditingFloorBidderBadge();
      showUserActivityLog();
    } else {
      setUserActivityLog('Lane Suspended');
    }

    setUserActivityLog('End handleDecision');
    showUserActivityLog();
  };

  handleDecisionKeyDown = (e, obj) => {
    const mapping = {
      F12: 'NoSale',
      PD: 'SubjectTo',
      PU: 'Sold',
    };

    if (obj) {
      const hasKey = mapping.hasOwnProperty(obj.label);

      /** TOM
       * Added this to show the modal window if the high bid is below the reserve threshold.
       * Conditions are....
       * PU or Enter button were hit
       * Lane IS suspended
       * current bid < reserveNotMetAlertThreshold
       */
      const {auction, laneSuspended, auctionProduct} = this.bidStore;
      const {currentBid, confirmModal} = auction;
      const {reserveNotMetAlertThreshold} = auctionProduct || {};

      if (obj.label === "PU" && laneSuspended && (currentBid < reserveNotMetAlertThreshold)) {
        if (confirmModal) {
          confirmModal.open();
          return
        }
      }

      /** END */

      if (hasKey) {
        e.preventDefault();
        this.handleDecision(mapping[obj.label]);
      }
    }
  };

  // TODO: If is open for bidding, don't allow for hot keys to trigger the decision button clicks
  handleKeyDown = (e) => {
    const code = getKeyCode(e);
    console.log(code)
    const obj = this.getHotKey('code', code);
    const GOING_ONCE = 118;
    const GOING_TWICE = 119;
    const ABOUT_TO_CLOSE = 120;
    const SELLING_ALL_THE_WAY = 121;
    const RERUN = 116;
    const RESUME = 117;
    const FLOOR_BID = 107;
    const RETRACT_BID = 109;
    const BID_INC_FIFTY = 112;
    const BID_INC_ONE_HUNDRED = 113;
    const BID_INC_TWO_HUNDRED = 114;
    const BID_INC_FIVE_HUNDRED = 115;
    const FLOOR_BID_OVERRIDE = 36;

    const laneMessageKeyCodes = [
      GOING_ONCE,
      GOING_TWICE,
      ABOUT_TO_CLOSE,
      SELLING_ALL_THE_WAY,
    ];

    const monitorChatOnly = toBoolean(getStoredValue('monitorChatOnly'));

    if (obj && !monitorChatOnly) {
      // Want to disable all default function keys for the browser when on clerk screen

      [
        BID_INC_FIFTY,
        BID_INC_ONE_HUNDRED,
        BID_INC_TWO_HUNDRED,
        BID_INC_FIVE_HUNDRED,
        GOING_ONCE,
        GOING_TWICE,
        ABOUT_TO_CLOSE,
        SELLING_ALL_THE_WAY,
        RERUN,
        RESUME,
        FLOOR_BID,
        RETRACT_BID,
        FLOOR_BID_OVERRIDE,
      ].forEach((fKey) => {
        if (code === fKey) {
          console.log(fKey);
          e.preventDefault();
          if (laneMessageKeyCodes.includes(fKey)) {
            this.handleSendLaneMessage(obj.label);
          } else if (fKey === RERUN) {
            this.handleReRun();
          } else if (fKey === RESUME) {
            this.handleResume();
          } else if (fKey === FLOOR_BID) {
            this.handleFloorBidderBidClick();
          } else if (fKey === RETRACT_BID) {
            this.handleRetractBid();
          }
          else if (fKey === FLOOR_BID_OVERRIDE) {
            console.log('FLOOR_BID_OVERRIDE')
            this.handleFloorBidderOverride();
          }
          else {
            this.handleBidIncrementClick(obj.target);
            if (this.bidStore.auction.selectedBidIncrementTabIndex === 0) {
              this.handleBidIncrementTabClick(1);
            }
          }
        }
      });

      this.handleDecisionKeyDown(e, obj);
    }
  };

  getHotKey = (key, value) => {
    const { keyMap } = this.auctionStore.settings;
    return keyMap.filter((item) => item[key] === value)[0];
  };

  handleCurrencyKeyUpFormat = (e) => {
    const numericValues = stripCurrencySymbols(e.currentTarget.value);
    // TODO: formatting is buggy. when we leave input, the commas are not formatted correctly.
    // const formattedNum = formatCurrency(numericValues);
    e.currentTarget.value = numericValues > 0 ? `$${numericValues}` : '';
  };

  handleFloorBidderBadgeKeyUp = (e) => {
    const { auction } = this.bidStore;
    this.bidStore.setSelectFloorBidderBadge(false);

    const SOLD = 33;
    const IF_SALE = 34;
    const NO_SALE = 123;
    let code = getKeyCode(e);

    if (isEnterKey(e)) {
      if (auction.firstDecisionClick === 'Sold') code = SOLD;
      if (auction.firstDecisionClick === 'SubjectTo') code = IF_SALE;
      if (auction.firstDecisionClick === 'NoSale') code = NO_SALE;
    }
    const obj = this.getHotKey('code', code);
    this.handleDecisionKeyDown(e, obj);
  };

  handleRejectFBBValidation = (e) => {
    const { setConfirmBadgeIdState, setSelectFloorBidderBadge } = this.bidStore;

    setConfirmBadgeIdState(true, false, null);
    this.setEditingFloorBidderBadge();
    setSelectFloorBidderBadge(true);
    e.preventDefault();
  };

  handleProceedFBBValidation = () => {
    const { resetConfirmations, setConfirmBadgeIdState } = this.bidStore;

    setConfirmBadgeIdState(false, true, null);
    const currentUiState = UiMessageCodeHelpers.getUiStateResult(
      this.bidStore.auction.uiDecisionCode,
    );

    this.handleCloseBidding(currentUiState.key);
    this.setEditingFloorBidderBadge();
    resetConfirmations();
  };

  handleFloorBidderBadgeFocus = () =>
    this.webSocketStore.sendFocusFloorBidderBadgeMessage();

  handleOpenItemFocus = () => this.webSocketStore.sendFocusOpenItemMessage();

  handleStartingBidFocus = () =>
    this.webSocketStore.sendFocusStartingBidAmountMessage();

  handleRemovalDefaultFocus = () =>
    this.webSocketStore.sendRemoveFocusMessage();

  handleOpenItemKeyPress = (e) => {
    const itemNumber = this.bidStore.auction.openItemNumberEntered;

    if (isEnterKey(e) && itemNumber !== '') {
      e.preventDefault();
      this.handleOpenItem(itemNumber);
      this.bidStore.setOpenItemNumberEntered('');
      this.webSocketStore.sendFocusOpenItemMessage();
    }
  };

  handleOpenItem = () => {
    if (!this.bidStore.auction.isOpenForBidding) {
      const { openItemNumberEntered } = this.bidStore.auction;
      this.webSocketStore.sendMessage({
        method: 'SetOnBlock',
        payload: [
          this.auctionStore.getAuctionLaneIdForInvocation,
          openItemNumberEntered,
        ],
      });

      this.bidStore.setOpenItemNumberEntered('');
      // BRIAN/TOM CONTINUE WITH ISOPENFORBIDDING ISSUE
      this.webSocketStore.sendFocusStartingBidAmountMessage();
    }
  };

  handleOpenItemClick = (value) => {
    if (!this.bidStore.auction.isOpenForBidding) {
      this.bidStore.setOpenItemNumberEntered(value);
      this.handleOpenItem();
    }
  };

  handleOpenItemChange = (e) =>
    this.bidStore.setOpenItemNumberEntered(e.currentTarget.value);

  handleStartingBidKeyPress = async (e) => {
    const value = this.bidStore.auction.startingBidAmountEntered;

    if (
      isEnterKey(e) &&
      value !== '' &&
      !this.bidStore.shouldDisableStartingAmountButton
    ) {
      this.handleStartingBidClick(getTargetValue(e));
    }
  };

  handleStartingBidClick = async (value) => {
    const minStartPrice = this.bidStore.auctionProduct?.minStartPrice || 0;
    const numericValue = +stripCurrencySymbols(value);

    if (numericValue >= minStartPrice) {
      this.bidStore.setStartingBidAmountEntered('');
      this.handleStartBidding(value);
      this.webSocketStore.sendFocusBidButtonMessage();
    } else {
      this.webSocketStore.sendFocusStartingBidAmountMessage();
    }
  };

  handleStartingBidAmountChange = (e) =>
    this.bidStore.setStartingBidAmountEntered(getTargetValue(e));

  handleOpenPrivateNotes = (isOpen, message) => {
    this.bidStore.setProperty('auctioneerOpenPublicComments', {
      message: null,
      show: !isOpen,
    });
    this.bidStore.setProperty('auctioneerOpenPrivateNotes', {
      message,
      show: isOpen,
    });
  };

  handleOpenPublicComments = (isOpen, message) => {
    this.bidStore.setProperty('auctioneerOpenPublicComments', {
      message,
      show: isOpen,
    });
    this.bidStore.setProperty('auctioneerOpenPrivateNotes', {
      message: null,
      show: !isOpen,
    });
  };

  handleClosePopoutComments = () => {
    this.bidStore.setProperty('auctioneerOpenPublicComments', {
      message: null,
      show: false,
    });
    this.bidStore.setProperty('auctioneerOpenPrivateNotes', {
      message: null,
      show: false,
    });
  };

  handleBidderStatusClick = (e, laneId, account, member, viewOnly) => {
    e.preventDefault();
    if (viewOnly) {
      this.webSocketStore.sendClearClientViewOnlyMessage(
        laneId,
        account,
        member,
      );
    } else {
      this.webSocketStore.sendSetClientViewOnlyMessage(laneId, account, member);
    }

    ReactGA.event({
      action: `Lane ID: ${laneId}; Account: ${account}; Member: ${member}; Set View Only: ${
        viewOnly ? 'YES' : 'NO'
      }`,
      category: 'Authorization',
      label: 'Enable/Disable Member',
      nonInteraction: false,
    });
  };

  handleChatFilter = (laneId, filterType, filterValue) => {
    const {
      directMessageLog,
      handleSetFilteredMessageData,
    } = this.messageStore;

    this.bidStore.setProperty(laneId, 'chatFilter', {
      filterType,
      filterValue,
    });

    const getMessagesThatContainValue = (type, value) =>
      directMessageLog.filter((message) =>
        trimmedStringContainsValue(message[type], value),
      );

    const getMessagesThatEqualValue = (type, value) =>
      directMessageLog.filter((message) =>
        trimmedStringMatchesExactly(message[type], value),
      );

    const getFilteredData = (type, value) => {
      const containsTextProps = ['content'];
      let filteredMessages = [];

      if (arrayContainsValue(containsTextProps, type)) {
        filteredMessages = getMessagesThatContainValue(type, value);
      } else {
        filteredMessages = getMessagesThatEqualValue(type, value);
      }

      return filteredMessages;
    };

    const isGlobalMessage = !!filterType && !filterValue;
    const filterHasTypeAndValue = !!filterType && !!filterValue;
    if (filterHasTypeAndValue || isGlobalMessage) {
      handleSetFilteredMessageData(getFilteredData(filterType, filterValue));
    } else {
      handleSetFilteredMessageData(directMessageLog);
    }
  };

  handleMonitorChatOnly = (value) => {
    window.localStorage.setItem('monitorChatOnly', value);
    this.bidStore.setProperty('monitorChatOnly', value);
  };

  handleStorageFontSize = (config) => {
    const { boundary, prop, type, value } = config;

    const setStoredValue = (prop, value) => {
      window.localStorage.setItem(prop, value);
      this.bidStore.setProperty(prop, value);
    };

    const getStoredValue = (prop) =>
      toNumber(window.localStorage.getItem(prop));

    const getConfigValue = () => {
      if (type === 'setValue') return toNumber(value);
      const lowerBounds = boundary[0];
      return toNumber(value) ?? toNumber(lowerBounds);
    };

    const storedValue = getStoredValue(prop);
    const newValue = getConfigValue() + storedValue;

    if (type === 'setValue') {
      setStoredValue(prop, value);
      return;
    }

    // incDecValue
    const [lowerBounds, upperBounds] = boundary;
    if (newValue >= lowerBounds && newValue <= upperBounds) {
      setStoredValue(prop, newValue);
      return;
    }
    const fontValue = newValue > lowerBounds ? upperBounds : lowerBounds;
    setStoredValue(prop, fontValue);
  };

  handleZoomLevel = (value) => {
    ReactGA.event({
      action: `Zoom Level: ${value}`,
      category: 'View',
      label: 'Zoom Level',
      nonInteraction: false,
    });

    this.bidStore.setProperty('zoomLevel', value);
  };
}

decorate(UIStore, {
  getHotKey: action,
  handleBidIncrementClick: action,
  handleBidIncrementTabClick: action,
  handleBidderStatusClick: action,
  handleChatFilter: action,
  handleClearLanePinMessage: action,
  handleCloseBidding: action,
  handleClosePopoutComments: action,
  handleCurrencyKeyUpFormat: action,
  handleDecision: action,
  handleDecisionClick: action,
  handleDecisionKeyDown: action,
  handleEndEvent: action,
  handleEndLane: action,
  handleFloorBidderBadgeChange: action,
  handleFloorBidderBadgeFocus: action,
  handleFloorBidderBadgeKeyUp: action,
  handleFloorBidderBidClick: action,
  handleFloorBidderOverride: action,
  handleInitializeScreenAuctions: action,
  handleInitializeScreenLanes: action,
  handleJoinAuction: action,
  handleJoinLane: action,
  handleKeyDown: action,
  handleLogin: action,
  handleLogout: action,
  handleMonitorChatOnly: action,
  handleOpenItem: action,
  handleOpenItemChange: action,
  handleOpenItemClick: action,
  handleOpenItemFocus: action,
  handleOpenItemKeyPress: action,
  handleOpenPrivateNotes: action,
  handleOpenPublicComments: action,
  handlePauseLane: action,
  handleProceedFBBValidation: action,
  handleReJoinLastLane: action,
  handleReRun: action,
  handleRemovalDefaultFocus: action,
  handleResume: action,
  handleResumeBidding: action,
  handleRetractBid: action,
  handleSendDirectMessage: action,
  handleSendLaneMessage: action,
  handleSetLanePinMessage: action,
  handleSetReserveToWinningBidAmount: action,
  handleStartBidding: action,
  handleStartEvent: action,
  handleStartingBidAmountChange: action,
  handleStartingBidClick: action,
  handleStartingBidFocus: action,
  handleStartingBidKeyPress: action,
  handleStorageFontSize: action,
  handleToggleLaneMedia: action,
  handleVAToggle: action,
  handleExtendVATime: action,
  handlePlayAnnouncements: action,
  handleStopAnnouncements: action,
  initialize: action,
});
